<template>
  <div
    class="magic-ui"
    :class="{'magic-ui-universal': universalBgImage}"
  >
    <div
      v-if="universalBgImage"
      class="magic-ui-universal-img-wrapper"
      :class="{scale: foldWidthRange}"
      :style="universalStyle"
    >
      <img
        :src="universalBgImage"
        width="100%"
      >
    </div>

    <magic-ui-page
      v-if="pageConfig"
      :config="pageConfig"
    />
    <slot />
  </div>
</template>

<script>
import delegate from 'delegate';
import { getUrlParam } from '@tencent/magic-ui-util';
import { request, getRequestDataReq } from '../../../src/request';

import { setActVmToContextInterface } from '@/svelte-comp-adapter';

// eslint-disable-next-line func-style
const computeRootFontSize = (envName, rootWidth) => {
  if (envName === 'tv') {
    return (rootWidth || 1920) * 100 / 1920;
  }
  return rootWidth / 3.75;
};

let popTimeoutId = null;

export default {
  name: 'MagicUi',

  provide() {
    return {
      app: this,
    };
  },

  props: {
    config: {
      type: Array,
      default: () => ([]),
    },

    pageConfig: {
      type: Object,
      default: () => ({}),
    },

    envName: {
      type: String,
      default: 'phone',
    },

    size: {
      type: String,
      default: 'vft_regular',
    },
  },

  data() {
    return {
      pages: [],
      containers: [],
      components: [],
      form: [],
      pops: [],
      openPopList: [],
      curPop: '',
      actInfo: {},
      eventQueueMap: {},
      globalData: {
        // 组件、插件数据存储区域，要求首层key名为组件/插件名，避免冲突，如{'magic-ui-pay-button':{'key1':'value1'}}
      },
      isInMagicFirstViewRender: {},
      isInMagicPageRender: {},
      magicPageViewPromise: {},
    };
  },

  computed: {
    appConfig() {
      return this.config[0] || {};
    },

    id() {
      return this.appConfig.id;
    },

    universalBgImage() {
      let img = '';
      if (this.envName !== 'phone' || this.size !== 'vft_regular') {
        img = this.pageConfig?.universalBgImage;
      }

      if (this.inMagic) {
        return img;
      }

      if (img && !this.inHippy) {
        window.document.documentElement.style.fontSize = '100px';
      } else if (this.pageConfig?.isLandscape && !this.inHippy) {
        const documentWidth = document.documentElement.getBoundingClientRect().width;
        const fontSize = computeRootFontSize(this.envName, documentWidth);
        window.document.documentElement.style.fontSize = `${fontSize}px`;
      } else if (getUrlParam('__no_magic_qrcode')) {
        // 尝试修复PC客户端下组件位置定位异常问题
        // window.document.documentElement.style.fontSize = '100px';
        return '';
      }

      return img;
    },

    universalStyle() {
      const style = {};
      if (this.pageConfig?.style.height) {
        style.height = `${this.pageConfig.style.height / 100}rem`;
      }

      return style;
    },

    foldWidthRange() {
      // 如果落在 large 范围里，则认为是折叠屏
      let foldWidthRange = this.size === 'vft_large';
      // 在魔方编辑器里，环境是 pad(pad横屏也在折叠屏范围内) fold 认为是折叠屏
      if (this.envName === 'fold' || (this.envName === 'pad' && this.inMagic)) {
        foldWidthRange = true;
      }

      return foldWidthRange;
    },
  },

  created() {
    setActVmToContextInterface(this);

    if (this.inMagic || this.inPrerender) {
      return;
    }

    if (this.env.isTv && !this.inHippy) {
      window.addEventListener('hashchange', () => {
        const { hash } = window.location;
        if (!hash) {
          this.closePop();
        } else {
          hash.replace(/magic-ui-pop_(\d+)/, (match, p1) => {
            if (!p1) {
              this.closePop();
            } else if (this.openPopList.find(pop => `${pop.name}` === p1)) {
              this.closePop();
            }
          });
        }
      });
    }

    // 根据配置绑定事件
    this.bindEvents();
  },

  methods: {
    async request(name, options) {
      // eslint-disable-next-line prefer-const
      let { data, req } = getRequestDataReq(this.pageConfig, name, options);

      if (data !== undefined) {
        return data;
      }

      return request(req);
    },

    getStorage(key) {
      if (this.$store?.state.storage) {
        return this.$store.state.storage[key];
      }
      return '';
    },

    setStorage(key, value) {
      return this.$store.dispatch('setStorage', { key, value });
    },

    magicPageViewReady() {
      return this.magicPageViewPromise[this.pageConfig.name] || Promise.resolve();
    },

    addPage(page) {
      this.add('pages', page);
    },

    addContainer(container) {
      this.add('containers', container);
    },

    addComponent(comp) {
      this.add('components', comp);
    },

    addForm(form) {
      this.add('form', form);
    },

    addPop(pop) {
      this.add('pops', pop);
    },

    add(type, item) {
      if (!item) {
        return item;
      }

      // 添加 svelte 组件时，会遇到 Symbol 转换的问题
      // 这里其实不明白为什么一开始要放到 `data` 中，明显会带来一系列的性能问题
      // 感觉直接放到 this 上就行了，但是其他地方有用到 `app`，为了最小化影响原有逻辑，这里保持不变
      try {
        this[type].push(item);
      } catch (e) {
        // ignore
        // 这里忽略添加 svelte 组件时带来的报错
      }

      const eventConfigQueue = this.eventQueueMap[item.config.id] || [];

      for (
        let eventConfig = eventConfigQueue.shift();
        eventConfig;
        eventConfig = eventConfigQueue.shift()
      ) {
        const { config, fromComponent, args } = eventConfig;
        if (config.method.startsWith('magic:common:actions:')) {
          item.$once('hook:mounted', () => {
            this.eventHandler({ ...config }, fromComponent, args);
          });
        } else {
          this.eventHandler({ ...config }, fromComponent, args);
        }
      }

      return item;
    },

    getPage(name) {
      let iName = name;
      if (!iName) {
        iName = this.page || getUrlParam('page');
      }
      return this.get('pages', iName) || this.pages[0];
    },

    getContainer(name) {
      return this.get('containers', name);
    },

    getComponent(name) {
      return this.get('components', name);
    },

    getForm(name) {
      return this.get('form', name);
    },

    getPop(name) {
      return this.get('pops', name);
    },

    get(type, name) {
      let items = [...this[type]];
      if (name) {
        const iName = typeof name === 'string' ? decodeURIComponent(name) : name;
        items = items.filter(item => item.config.name === iName || item.config.id === iName);
      }
      return items.length ? items[0] : null;
    },

    removePage(page) {
      return this.remove('pages', page);
    },

    removeContainer(container) {
      return this.remove('containers', container);
    },

    removeComponent(component) {
      return this.remove('components', component);
    },

    removeForm(form) {
      return this.remove('form', form);
    },

    removePop(pop) {
      return this.remove('pops', pop);
    },

    remove(type, comp) {
      const allComps = this[type];
      const index = allComps.indexOf(comp);
      if (index === -1) {
        return;
      }

      allComps.splice(index, 1);
    },

    openPop(name, data) {
      console.log(name);
      this.openPopList.push({ name, data });

      if (popTimeoutId) {
        clearTimeout(popTimeoutId);
      }

      popTimeoutId = setTimeout(() => {
        const nextPop = this.openPopList.pop();

        if (this.curPop) {
          const pop = this.getPop(this.curPop.name);
          if (pop) {
            pop.visible = false;
          }
          this.openPopList.push(this.curPop);
        }

        const pop = this.getPop(nextPop.name);
        if (pop?.open(nextPop.data)) {
          this.curPop = nextPop;
          if (this.env.isTv && !this.inHippy) {
            const { origin, pathname, search } = window.location;
            window.location.href = `${origin}${pathname}${search}#magic-ui-pop_${pop.config.id}`;
          }
          this.$emit('pop-open', pop.config);
        }
      });
    },

    async closePop() {
      if (!this.curPop) {
        return;
      }
      const pop = this.getPop(this.curPop.name);
      if (await pop?.close()) {
        this.$emit('pop-close', pop.config);

        this.curPop = null;

        if (this.env.isTv && !this.inHippy) {
          window.history.back();
        }

        if (this.openPopList.length) {
          const nextPop = this.openPopList.pop();
          this.openPop(nextPop.name, nextPop.data);
        }
      }
    },

    addEventToMap({ method, from, to }, fromComponent, args) {
      if (this.eventQueueMap[to]) {
        this.eventQueueMap[to].push({
          config: { method, from, to },
          fromComponent,
          args,
        });
      } else {
        this.eventQueueMap[to] = [{
          config: { method, from, to },
          fromComponent,
          args,
        }];
      }
    },

    bindEvents() {
      const eventMap = {};

      // eslint-disable-next-line func-style
      function searchEvents(items) {
        if (!Array.isArray(items)) {
          return;
        }
        items.forEach((i) => {
          Array.isArray(i.events) && i.events.forEach((e) => {
            if (!e.name || !e.to || !e.method) return;
            const eventName = e.name;
            if (!eventMap[eventName]) {
              eventMap[eventName] = [];
            }
            eventMap[eventName].push({
              from: i.id,
              to: e.to,
              method: e.method,
            });
          });
          i.pop?.items && searchEvents(i.pop.items);
          return searchEvents(i.items);
        });
      }

      this.config && searchEvents(this.config);
      // eslint-disable-next-line no-restricted-syntax
      for (const [name, event] of Object.entries(eventMap)) {
        if (name.startsWith('magic:common:events:') && !this.inHippy) {
          const eventName = name.split(':').pop();
          if (['logined', 'logout', 'isvip', 'notvip'].includes(eventName)) {
            this.$bus.$on(name, (fromCpt, ...args) => {
              event.forEach(e => this.eventHandler(e, fromCpt, args));
            });
            continue;
          }
          event.forEach(({ from, method, to }) => {
            const useCapture = eventName === 'click.capture';
            delegate(`[id="${from}"]`, 'click', (nativeEvent) => {
              this.eventHandler({ from, to, method }, null, [nativeEvent]);
            }, useCapture);
          });
          continue;
        }
        this.$bus.$on(name, (fromCpt, ...args) => {
          event.forEach((e) => {
            if (`${e.from}` !== `${fromCpt.config.id}`) return;
            this.eventHandler(e, fromCpt, args);
          });
        });
      }
    },

    eventHandler({ method, from, to }, fromComponent, args) {
      if (this.inHippy && method === 'magic:common:actions:scrollToTop') {
        this.$el.scrollTo(0, 0);
        return;
      }

      if (!this.inHippy && method.startsWith('magic:common:actions:')) {
        // 直接操作dom动作
        const el = document.getElementById(to);
        if (!el) {
          this.addEventToMap({ method, from, to }, fromComponent, args);
          return;
        }
        ;
        const action = method.split(':').pop();
        // if (!['show', 'hide', 'scrollIntoView', 'scrollToTop'].includes(action)) return
        if (action === 'show') {
          el.style.display = el.dataset.display || 'block';
          return;
        }
        if (action === 'hide' && el.style.display !== 'none') {
          el.dataset.display = el.style.display;
          el.style.display = 'none';
          return;
        }
        if (action === 'scrollIntoView') return el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (action === 'scrollToTop') return window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // vue组件动作
      const allComponents = [...this.components, ...this.containers, ...this.pages, ...this.pops];
      const fromCpt = fromComponent || allComponents.find(com => com.config && `${com.config.id}` === `${from}`);
      if (!fromCpt) return;
      let targetCpt = allComponents.find(com => com.config && `${com.config.id}` === `${to}`);
      if (targetCpt?.$refs?.magicComp) {
        targetCpt = targetCpt.$refs.magicComp;
      }
      if (!targetCpt || typeof targetCpt[method] !== 'function') {
        this.addEventToMap({ method, from, to }, fromComponent, args);
        return;
      }

      // svelte 组件直接调用其方法，将原始参数传入，不传入来源组件
      if (targetCpt.isSvelte) {
        targetCpt[method](...args);
      } else {
        targetCpt[method](fromCpt, ...args);
      }
    },

    openUrl(url, ...args) {
      if (typeof this.$magicOpenUrl === 'function') {
        this.$magicOpenUrl(url, ...args);
      } else {
        this.toast('没有打开URL的插件');
      }
    },
  },
};
</script>
